<template>
  <div class="fixed top-0 h-screen font-inter flex flex-col w-[294px] bg-primaryBg text-primaryDark">
    <!-- Header Section -->
    <div class="flex-shrink-0 px-4 mt-6 space-y-5 mb-5">
      <div class="space-y-4">
        <div class="font-medium text-bodyText text-[16px] flex items-center space-x-4">
          <p class="px-2 py- bg-[#E5E7ED] rounded-[6px]">PT</p>
          <p class="">Admin Portal</p>
          <div class="flex items-center w-[10px]">
            <button @click="goBack" aria-label="Back"
              class="p-1.5 rounded-md text-gray-500 hover:text-primaryDarkBg hover:bg-[#F0F2F5] focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-[#E5E7ED]">
              <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.9998 19.9201L8.47984 13.4001C7.70984 12.6301 7.70984 11.3701 8.47984 10.6001L14.9998 4.08008" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <button @click="goForward" aria-label="Forward"
              class="ml-1 p-1.5 rounded-md text-gray-500 hover:text-primaryDarkBg hover:bg-[#F0F2F5] focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-[#E5E7ED]">
              <svg class="w-6 h-6 transform rotate-180" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.9998 19.9201L8.47984 13.4001C7.70984 12.6301 7.70984 11.3701 8.47984 10.6001L14.9998 4.08008" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>          
        </div>
      </div>
      <hr class="w-[254px] border-t-[1px] border-[#E5E7ED]">
    </div>

    <!-- Scrollable Navigation Section -->
    <div class="flex-1 overflow-y-auto px-4">
      <nav>
        <router-link v-for="item in nonGoalsItems" :key="item.path" :class="[
          'flex items-center w-[254px] h-10 px-3 py-2 gap-2 mb-2 text-[14px] font-medium rounded-lg transition-colors',
          isActiveLink(item.path)
            ? 'bg-[#F0F2F5] text-primaryDarkBg'
            : 'text-grayDark hover:bg-[#F0F2F5] hover:text-primaryDarkBg'
        ]" :to="item.path">
          <component :is="getIconComponent(item.icon)" class="w-5 h-5" />
          {{ item.name }}
        </router-link>

        <div v-if="hasExploreItem">
          <div @click="toggleExploreExpanded" :class="[
            'flex items-center w-[254px] h-10 px-3 py-2 gap-2 mb-2 text-[14px] font-medium rounded-lg transition-colors cursor-pointer',
            (isActiveLink('/explore') || isExploreRelatedRoute)
              ? 'bg-[#F0F2F5] text-primaryDarkBg'
              : 'text-grayDark hover:bg-[#F0F2F5] hover:text-primaryDarkBg'
          ]">
            <component :is="getIconComponent('explore')" class="w-5 h-5" />
            Explore
            <svg class="ml-auto" width="12" height="12" viewBox="0 0 12 12" fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path :d="exploreExpanded ? 'M2 8L6 4L10 8' : 'M2 4L6 8L10 4'" stroke="currentColor" stroke-width="1.5"
                stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>

          <div v-if="exploreExpanded" class="ml-6 mb-2">
            <router-link v-for="item in getExploreSubItems()" :key="item.path" :to="item.path" :class="[
              'flex items-center w-[230px] h-8 px-3 py-1 gap-2 mb-1 text-[13px] font-medium rounded-lg transition-colors ',
              $route.path === item.path
                ? 'bg-[#F0F2F5] text-primaryDarkBg'
                : 'text-grayDark hover:bg-[#F0F2F5] hover:text-primaryDarkBg'
            ]">
              {{ item.name }}
            </router-link>
          </div>
        </div>

        <!-- Goals expandable section -->
        <div v-if="hasGoalsItem">
          <div @click="toggleGoalsExpanded" :class="[
            'flex items-center w-[254px] h-10 px-3 py-2 gap-2 mb-2 text-[14px] font-medium rounded-lg transition-colors cursor-pointer',
            (isActiveLink('/projects') || isGoalsRelatedRoute)
              ? 'bg-[#F0F2F5] text-primaryDarkBg'
              : 'text-grayDark hover:bg-[#F0F2F5] hover:text-primaryDarkBg'
          ]">
            <component :is="getIconComponent('project')" class="w-5 h-5" />
            Goals
            <svg class="ml-auto" width="12" height="12" viewBox="0 0 12 12" fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path :d="goalsExpanded ? 'M2 8L6 4L10 8' : 'M2 4L6 8L10 4'" stroke="currentColor" stroke-width="1.5"
                stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>

          <!-- Expanded project list -->
          <div v-if="goalsExpanded" class="ml-6 mb-2">
            <div v-if="activeProjects.length > 0">
              <router-link v-for="project in activeProjects" :key="project.id" :to="`/projects/${project.id}`" :class="[
                'flex items-center w-[230px] h-8 px-3 py-1 gap-2 mb-1 text-[13px] font-medium rounded-lg transition-colors ',
                $route.params.projectId === project.id
                  ? 'bg-[#F0F2F5] text-primaryDarkBg'
                  : 'text-grayDark hover:bg-[#F0F2F5] hover:text-primaryDarkBg'
              ]">
                {{ project.name }}
              </router-link>
            </div>
            <div v-else-if="isLoading" class="px-3 py-2 text-[13px] text-gray-500">
              Loading projects...
            </div>
            <div v-else-if="!hasRequiredRole" class="px-3 py-2 text-[13px] text-gray-500">
              You don't have a talent, manager, or investor account.
            </div>
            <div v-else class="px-3 py-2 text-[13px] text-gray-500">
              No active projects found.
            </div>

            <router-link v-if="activeProjects.length > 0 || hasRequiredRole" to="/projects"
              class="flex items-center justify-between w-[230px] h-8 px-3 py-1 gap-2 mb-1 mt-2 text-[13px] font-semibold rounded-lg transition-colors text-primaryDarkBg hover:bg-[#F0F2F5] border-t border-gray-200 pt-2">
              <span>View all goals</span>
              <span v-if="allProjects.length > 5" class="px-2 py-0.5 bg-gray-200 text-gray-700 text-[11px] font-semibold rounded-md">
                {{ allProjects.length - 5 }}+
              </span>
            </router-link>
          </div>
        </div>

        <!-- Remaining menu items after Goals -->
        <router-link v-for="item in afterGoalsItems" :key="item.path" :class="[
          'flex items-center w-[254px] h-10 px-3 py-2 gap-2 mb-2 text-[14px] font-medium rounded-lg transition-colors',
          isActiveLink(item.path)
            ? 'bg-[#F0F2F5] text-primaryDarkBg'
            : 'text-grayDark hover:bg-[#F0F2F5] hover:text-primaryDarkBg'
        ]" :to="item.path">
          <component :is="getIconComponent(item.icon)" class="w-5 h-5" />
          {{ item.name }}
        </router-link>

      </nav>
    </div>

    <!-- Bottom Section for "Support" and "Settings" - This will stick to the bottom -->
    <div class="flex-shrink-0 px-4 pb-4">
      <router-link v-for="item in sidebarItem2" :key="item.path" :class="[
        'flex items-center w-[254px] h-10 px-3 py-2 gap-2 text-[14px] font-medium rounded-lg text-grayDark hover:bg-[#F0F2F5] hover:text-primaryDarkBg focus:outline-none focus:shadow-outline',
        isActiveLink(item.path) ? 'bg-[#F0F2F5] text-primaryDarkBg' : ''
      ]" :to="item.path">
        <component :is="getIconComponent(item.icon)" class="" />
        {{ item.name }}
      </router-link>
    </div>
  </div>
</template>

<script>
import eventBus from '@/eventBus';
import ExploreIcon from './svg-icons/ExploreIcon.vue';
import ExploreActiveIcon from './svg-icons/ExploreActiveIcon.vue';
import ActivityDumpIcon from './svg-icons/ActivityDumpIcon.vue';
import DashboardIcon from './svg-icons/DashboardIcon.vue';
import DashboardActiveIcon from './svg-icons/DashboardActiveIcon.vue';
import UserIcon from '@/components/svg-icons/UserIcon.vue';
import UserActiveIcon from '@/components/svg-icons/UserActiveIcon.vue';
import ProjectIcon from '@/components/svg-icons/ProjectIcon.vue';
import FolderIcon from '@/components/svg-icons/FolderIcon.vue';
import ReportIcon from '@/components/svg-icons/ReportIcon.vue';
import TalentIcon from './svg-icons/TalentIcon.vue';
import ManagerIcon from './svg-icons/ManagerIcon.vue';
import SettingsIcon from './svg-icons/SettingsIcon.vue';
import SupportIcon from './svg-icons/SupportIcon.vue';
import InvestedProjectIcon from './svg-icons/InvestedProjectIcon.vue';
import AchievementsIcon from './svg-icons/AchievementsIcon.vue';
import AchievementsActiveIcon from './svg-icons/AchievementsActiveIcon.vue';
import LearningGoals from './svg-icons/LearningGoals.vue';
import TeamsIcon from './svg-icons/TeamsIcon.vue';
import TeamActiveIcon from './svg-icons/TeamActiveIcon.vue';
import BookIcon from './svg-icons/BookIcon.vue';
import InvitationRequestIcon from './svg-icons/InvitationRequestIcon.vue';
import BookingsIcon from './svg-icons/BookingsIcon.vue';
import BookingsActiveIcon from './svg-icons/BookingsActiveIcon.vue';
import TIIcon from './svg-icons/TIIcon.vue';
import TIActiveIcon from './svg-icons/TIActiveIcon.vue';
import apiClient from '@/axios';

export default {
  name: 'ProjectSidebar',
  components: {
    ExploreIcon,
    ExploreActiveIcon,
    BookIcon,
    DashboardIcon,
    DashboardActiveIcon,
    UserIcon,
    UserActiveIcon,
    ProjectIcon,
    FolderIcon,
    ReportIcon,
    TalentIcon,
    ManagerIcon,
    SettingsIcon,
    SupportIcon,
    InvestedProjectIcon,
    AchievementsIcon,
    AchievementsActiveIcon,
    LearningGoals,
    TeamsIcon,
    TeamActiveIcon,
    InvitationRequestIcon,
    ActivityDumpIcon,
    BookingsIcon,
    BookingsActiveIcon,
    TIIcon,
    TIActiveIcon
  },
  data() {
    return {
      goalsExpanded: false,
      learningGoalsExpanded: false,
      exploreExpanded: false,
      allProjects: [], // Store all projects
      activeProjects: [], // Store only active projects
      isLoading: false, // Loading state for projects
      lastUserRole: null, // Track the last user role to detect changes
    };
  },
  computed: {
    isActiveExplore() {
      return this.$route.path.startsWith('/explore');
    },
    isActiveDashboard() {
      return this.$route.path.startsWith('/dashboard');
    },
    isActiveProjects() {
      return this.$route.path.startsWith('/projects');
    },
    isActiveInvestedProjects() {
      return this.$route.path.startsWith('/invested-projects');
    },
    isActiveUsers() {
      return this.$route.path.startsWith('/users');
    },
    isActiveManagers() {
      return this.$route.path.startsWith('/managers');
    },
    isActiveTalents() {
      return this.$route.path.startsWith('/talents');
    },
    isActiveProgressReports() {
      return this.$route.path.startsWith('/progress-reports');
    },
    isActiveSettings() {
      return this.$route.path.startsWith('/settings');
    },
    isActiveInvitationRequests() {
      return this.$route.path.startsWith('/invitation-requests');
    },
    isActiveAchievements() {
      return this.$route.path.startsWith('/achievements');
    },
    isActiveTeams() {
      return this.$route.path.startsWith('/teams');
    },
    isActiveLearningGoals() {
      return this.$route.path.startsWith('/learning-goals');
    },
    isActiveActivityDump() {
      return this.$route.path.startsWith('/activity-dump');
    },
    isActiveBookings() {
      return this.$route.path.startsWith('/bookings');
    },
    isActiveTalentIntelligence() {
      return this.$route.path.startsWith('/talent-intelligence');
    },
    isActiveJobs() {
      return this.$route.path.startsWith('/jobs');
    },
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isManager() {
      return this.$store.getters.isManager;
    },
    isTalent() {
      return this.$store.getters.isTalent;
    },
    isInvestor() {
      return this.$store.getters.isInvestor;
    },
    isUser() {
      return this.$store.getters.isUser;
    },

    // Check if the user has any of the required roles for projects
    hasRequiredRole() {
      return this.isAdmin || this.isManager || this.isTalent || this.isInvestor;
    },

    // Current user role for change detection
    currentUserRole() {
      if (this.isAdmin) return 'admin';
      if (this.isManager) return 'manager';
      if (this.isTalent) return 'talent';
      if (this.isInvestor) return 'investor';
      return 'user';
    },

    // Check if the current route is related to a specific project goal
    isGoalsRelatedRoute() {
      return this.$route.path.startsWith('/projects/') && this.$route.params.projectId;
    },
    isLearningGoalsRelatedRoute() {
      return this.$route.path.startsWith('/learning-goals/')
    },
    isExploreRelatedRoute() {
      return this.$route.path.startsWith('/explore/');
    },


    // Check if the sidebar should include a Goals item
    hasGoalsItem() {
      return this.sidebarItem1.some(item => item.name === 'Goals');
    },
    hasLearningGoalsItem() {
      return this.sidebarItem1.some(item => item.name === 'Goals');
    },
    hasExploreItem() {
      return this.sidebarItem1.some(item => item.name === 'Explore');
    },

    // Get items that appear before Goals in the menu
    nonGoalsItems() {
      const exploreIndex = this.sidebarItem1.findIndex(item => item.name === 'Explore');
      const goalsIndex = this.sidebarItem1.findIndex(item => item.name === 'Goals');

      let endIndex;
      if (exploreIndex !== -1 && goalsIndex !== -1) {
        endIndex = Math.min(exploreIndex, goalsIndex);
      } else if (exploreIndex !== -1) {
        endIndex = exploreIndex;
      } else if (goalsIndex !== -1) {
        endIndex = goalsIndex;
      } else {
        return this.sidebarItem1;
      }

      return this.sidebarItem1.slice(0, endIndex);
    },

    // Get items that appear after Goals in the menu
    afterGoalsItems() {
      const exploreIndex = this.sidebarItem1.findIndex(item => item.name === 'Explore');
      const goalsIndex = this.sidebarItem1.findIndex(item => item.name === 'Goals');

      let startIndex;
      if (exploreIndex !== -1 && goalsIndex !== -1) {
        startIndex = Math.max(exploreIndex, goalsIndex) + 1;
      } else if (exploreIndex !== -1) {
        startIndex = exploreIndex + 1;
      } else if (goalsIndex !== -1) {
        startIndex = goalsIndex + 1;
      } else {
        return [];
      }

      return this.sidebarItem1.slice(startIndex);
    },

    sidebarItem1() {
      if (this.isAdmin) {
        return [
          { name: 'Home', path: '/dashboard', icon: 'dashboard' },
          { name: 'Goals', path: '/projects', icon: 'project' },
          { name: 'Jobs', path: '/jobs', icon: 'report' },
          { name: 'Users', path: '/users', icon: 'user' },
          { name: 'Reports', path: '/progress-reports', icon: 'report' },
          { name: 'Teams', path: '/teams', icon: 'team'},
          { name: 'Talent Intelligence', path: '/talent-intelligence', icon: 'talent-intelligence' },
          { name: 'Activity Dump', path: '/activity-dump', icon: 'activity-dump'},
        ];
      } else if (this.isManager || this.isTalent) {
        return [
          { name: 'Explore', path: '/explore', icon: 'explore' },
          { name: 'Goals', path: '/projects', icon: 'project' },
          { name: 'Home', path: '/dashboard', icon: 'dashboard' },
          { name: 'Talents', path: '/talents', icon: 'talent' },
          { name: 'Reports', path: '/progress-reports', icon: 'report' },
          { name: 'Achievements', path: '/achievements', icon: 'achievement' },
          { name: 'Invitation Requests', path: '/invitation-requests', icon: 'invitation-request'},
          { name: 'Teams', path: '/teams', icon: 'team'},
          { name: 'Talent Intelligence', path: '/talent-intelligence', icon: 'talent-intelligence' },
          { name: 'Learning Goals', path: '/learning-goals', icon: 'learning-goals' },
          { name: 'Bookings & Request', path: '/bookings', icon: 'bookings' }

        ];
      // } else if (this.isTalent) {
      //   return [
      //     { name: 'Explore', path: '/explore', icon: 'explore' },
      //     { name: 'Home', path: '/dashboard', icon: 'dashboard' },
      //     { name: 'Goals', path: '/projects', icon: 'project' },
      //     { name: 'My reports', path: '/progress-reports', icon: 'report' },
      //     // { name: 'Achievements', path: '/achievements', icon: 'achievement' },
      //     { name: 'Teams', path: '/teams', icon: 'team'},
      //     { name: 'Learning Goals', path: '/learning-goals', icon: 'learning-goals' }

      //   ];
      } else if (this.isInvestor) {
        return [
          { name: 'Explore', path: '/explore', icon: 'explore' },
          { name: 'Home', path: '/dashboard', icon: 'dashboard' },
          { name: 'Talent Intelligence', path: '/talent-intelligence', icon: 'talent-intelligence' },
          // { name: 'Projects', path: '/projects', icon: 'project' },
          { name: 'My Invested Projects', path: '/invested-projects', icon: 'invested-project' },
          // { name: 'Achievements', path: '/achievements', icon: 'achievement' },
        ];
      } else {
        return [
          { name: 'Explore', path: '/explore', icon: 'explore' },
          { name: 'Home', path: '/dashboard', icon: 'dashboard' },
          { name: 'Goals', path: '/projects', icon: 'project' },
        ];
      }
    },

    sidebarItem2() {
      return [
        { name: 'Support', path: '/support', icon: 'support' },
        { name: 'Settings', path: '/settings', icon: 'setting' },
      ];
    }
  },
  methods: {
    toggleGoalsExpanded() {
      this.goalsExpanded = !this.goalsExpanded;
    },

    toggleLearningGoalsExpanded() {
      this.learningGoalsExpanded = !this.learningGoalsExpanded;
    },

    toggleExploreExpanded() {
      this.exploreExpanded = !this.exploreExpanded;
      // Navigate to explore route when the dropdown is selected
      this.$router.push('/explore');
    },


    isActiveLink(path) {
      switch (path) {
        case '/explore':
          return this.isActiveExplore;
        case '/dashboard':
          return this.isActiveDashboard;
        case '/projects':
          return this.isActiveProjects;
        case '/invested-projects':
          return this.isActiveInvestedProjects;
        case '/users':
          return this.isActiveUsers;
        case '/managers':
          return this.isActiveManagers;
        case '/talents':
          return this.isActiveTalents;
        case '/progress-reports':
          return this.isActiveProgressReports;
        case '/settings':
          return this.isActiveSettings;
        case '/invitation-requests':
          return this.isActiveInvitationRequests;
        case '/activity-dump':
          return this.isActiveActivityDump;
        case '/bookings':
          return this.isActiveBookings;
        case '/talent-intelligence':
          return this.isActiveTalentIntelligence;
        case '/jobs':
          return this.isActiveJobs;
        case '/teams':
          return this.isActiveTeams;
        case '/achievements':
          return this.isActiveAchievements;
        case '/learning-goal':
          return this.isActiveLearningGoals;
        default:
          return this.$route.path === path;
      }
    },

    getIconComponent(iconName) {
      switch (iconName) {
        case 'explore':
          return this.isActiveExplore ? 'ExploreActiveIcon' : 'ExploreIcon';
        case 'dashboard':
          return this.isActiveDashboard ? 'DashboardActiveIcon' : 'DashboardIcon';
        case 'user':
          return this.isActiveUsers ? 'UserActiveIcon' : 'UserIcon';
        case 'manager':
          return 'ManagerIcon';
        case 'talent':
          return this.isActiveTalents ? 'TeamActiveIcon' : 'TeamsIcon';
        case 'talent-intelligence':
          return this.isActiveTalentIntelligence ? 'TIActiveIcon' : 'TIIcon';
        case 'project':
          return this.isActiveProjects ? 'ProjectIcon' : 'FolderIcon';
        case 'report':
          return this.isActiveProgressReports ? 'ActivityDumpIcon' : 'ReportIcon';
        case 'setting':
          return 'SettingsIcon';
        case 'support':
          return 'SupportIcon';
        case 'invested-project':
          return 'InvestedProjectIcon';
        case 'team':
          return this.isActiveTeams ? 'TeamActiveIcon' : 'TeamsIcon';
        case 'invitation-request':
          return 'InvitationRequestIcon';
        case 'activity-dump':
          return this.isActiveActivityDump ? 'ActivityDumpIcon' : 'ReportIcon';
        case 'bookings':
          return this.isActiveBookings ? 'BookingsActiveIcon' : 'BookingsIcon';
        case 'achievement':
          return this.isActiveAchievements ? 'AchievementsActiveIcon' : 'AchievementsIcon';
        case 'learning-goals':
          return this.isActiveLearningGoals ? 'LearningGoalsActiveIcon' : 'BookIcon';
        default:
          return 'ReportIcon'; // Default icon
      }
    },

    openCreateUserModal() {
      console.log('Button clicked, emitting event');
      eventBus.emit('openCreateUserModal');
    },

    goBack() {
      if (window && window.history && window.history.length > 1) {
        this.$router.go(-1);
      }
    },
    goForward() {
      this.$router.go(1);
    },

    async fetchUserProjects() {
      // Skip fetching if user doesn't have required role
      if (!this.hasRequiredRole) {
        this.activeProjects = [];
        this.allProjects = [];
        return;
      }

      this.isLoading = true;
      try {
        let endpoint = '';

        // Select the appropriate endpoint based on user role
        // if (this.isAdmin) {
        //   endpoint = '/web/projects_all';
        // } else if (this.isTalent) {
        //   endpoint = '/web/talent/me';
        // } else if (this.isManager) {
        //   endpoint = '/web/manager/me';
        if( this.isTalent || this.isManager || this.isAdmin) {
          endpoint = '/web/projects_all';
          // } else if (this.isInvestor) {
          //   endpoint = '/web/projects'; // Endpoint for investors
        } else {
          // No appropriate role
          this.isLoading = false;
          this.activeProjects = [];
          this.allProjects = [];
          return;
        }

        const response = await apiClient.get(endpoint,
          {
            params: {
            active_profile: this.isAdmin ? "admin" : this.isManager ? "manager" : this.isInvestor ? "investor" : this.isTalent ? "talent" : "",
          }
        }
        );

        let projectsData = [];
        if (this.isAdmin) {
          projectsData = response.data.data || [];
        } else if (this.isTalent || this.isManager) {
          // Check if projects are in a nested 'projects' property
          projectsData = response.data.data.projects || response.data.data || [];
        } else if (this.isInvestor) {
          projectsData = response.data.data || [];
        }

        // For debugging
        // console.log('Projects data:', projectsData);

        // Store all projects with consistent property names
        this.allProjects = projectsData.map(project => ({
          id: project.id || project.project_id,
          name: project.project_name || project.name,
          status: project.status || project.project_status || 'active' // Default to active if no status
        }));

        // For debugging
        // console.log('All projects after mapping:', this.allProjects);

        // Filter only active projects for the sidebar
        this.filterActiveProjects();

        // Auto-expand Goals if we're on a project page
        if (this.isGoalsRelatedRoute) {
          this.goalsExpanded = true;
        }
      } catch (error) {
        console.error('Error fetching user projects:', error);
        this.activeProjects = [];
        this.allProjects = [];
      } finally {
        this.isLoading = false;
      }
    },

    filterActiveProjects() {
      this.activeProjects = this.allProjects.slice(0, 5);
      // console.log('Active projects after filtering:', this.activeProjects);
    },

    // Check for role changes and refetch projects if needed
    checkForRoleChanges() {
      if (this.lastUserRole !== this.currentUserRole) {
        console.log('Role changed from', this.lastUserRole, 'to', this.currentUserRole);
        this.lastUserRole = this.currentUserRole;
        this.fetchUserProjects();
      }
    },

    // Get explore sub-navigation items
    getExploreSubItems() {
      return [
        { name: 'Projects', path: '/explore/projects' },
        { name: 'Users', path: '/explore/users' },
        // { name: 'Activity', path: '/explore/activity' },
        { name: 'Workspaces/Labs', path: '/explore/workspaces' },
        { name: 'Resources', path: '/explore/resources' },
        { name: 'Events', path: '/explore/events' },
        { name: 'News & Insights', path: '/explore/news' }
      ];
    }
  },
  created() {
    this.lastUserRole = this.currentUserRole;
    this.fetchUserProjects();

    // Listen for auth state changes
    eventBus.on('authStateChanged', () => {
      console.log('Auth state changed event received');
      this.checkForRoleChanges();
      this.fetchUserProjects(); // Always fetch projects on auth state change
    });
  },
  mounted() {
    // Set an interval to periodically check for role changes
    this.roleCheckInterval = setInterval(() => {
      this.checkForRoleChanges();
    }, 2000); // Check every 2 seconds
  },
  beforeUnmount() {
    // Clean up the interval when component is destroyed
    clearInterval(this.roleCheckInterval);

    // Remove event listener
    eventBus.off('authStateChanged');
  },
  watch: {
    '$route.path'() {
      // Auto-expand Goals section when navigating to project-related routes
      if (this.isGoalsRelatedRoute && !this.goalsExpanded) {
        this.goalsExpanded = true;
      }
      if (this.isLearningGoalsRelatedRoute && !this.learningGoalsExpanded) {
        this.learningGoalsExpanded = true;
      }
      if (this.isExploreRelatedRoute && !this.exploreExpanded) {
        this.exploreExpanded = true;
      }
    },

    // Watch for changes in the store's user role
    currentUserRole: {
      handler(newRole, oldRole) {
        if (newRole !== oldRole) {
          console.log('User role changed from', oldRole, 'to', newRole);
          this.fetchUserProjects();
        }
      },
      immediate: true
    }
  }
};
</script>